import { Alert, Box, Grid, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import ButtonGroup from 'components/button-group';
import FlexBox from 'components/flex-box';
import { useAccountState } from 'contexts/account/account-context';

import Checkbox from 'components/checkbox';
import CheckboxSelectItem from 'components/checkbox-select-item';
import Paper from 'components/paper';
import Select from 'components/select';
import { useGet } from 'hooks/use-get';
import { MarketId } from 'types/market-id.enum';
import { getDeliveryBoardItems } from 'views/delivery-board/api/delivery-board';
import DeliveryBoardMap from 'views/delivery-board/components/delivery-board-map';
import ColumnsContainer from './components/column-container';
import DeliveryColumnsSubTabs from './components/column-tabs';
import { OrderShipmentStatus } from './types/order-shipment-status.enum';

type Props = {};

const MapGrid = styled(Grid)(({ theme }) => ({
  width: '100%',
  height: '84vh',
  [`& .gm-style`]: {
    ['& .gm-style-iw']: {
      // info window styling
    },
    [`& .gm-style-iw-c`]: {
      [`& .gm-ui-hover-effect`]: {
        // override infowindow close button
        top: '-3px !important',
        right: '0px !important',
      },
    },
  },
}));

const DeliveryBoard: React.FC<Props> = ({}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { account } = useAccountState();
  const PAGE_NAME_ID = 'deliveryBoard';
  const [isMapVisible, setIsMapVisible] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  //#region STATES

  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');

  const [selectedVessels, setSelectedVessels] = useState<string[]>([]);
  const [vesselSearchValue, setVesselSearchValue] = useState<string>('');

  const [activeTab, setActiveTab] = useState<number>(2);
  const [showFactoryLocations, setShowFactoryLocations] = useState<boolean>(false);

  //#endregion STATES

  //#region FETCH DATA
  const { data: items, isLoading } = useGet(
    ['getDeliveryBoardItems', account.company?.customerErpId],
    ({ queryKey }) => getDeliveryBoardItems(String(queryKey[1])),
    {
      enabled: !!account.company?.customerErpId && account.company.marketId === MarketId.Norway,
    }
  );

  //#endregion FETCH DATA

  //#region MEMO

  const siteList = useMemo(() => new Map(items?.map((item) => [item.globalSiteId, item])), [items]);

  const siteIds = useMemo(() => {
    setSelectedSiteIds([...siteList.keys()]);
    return Array.from(siteList.keys());
  }, [siteList]);

  const sites = useMemo(() => Array.from(siteList, ([key, value]) => value), [siteList]);

  const filteredItems = useMemo(
    () => items?.filter((item) => selectedSiteIds.includes(item.globalSiteId)).filter((item) => selectedVessels.includes(item.vessel)),
    [items, selectedSiteIds, selectedVessels]
  );

  const loadingItems = useMemo(
    () =>
      _.chain(filteredItems)
        .filter((item) => item.status === OrderShipmentStatus.LOADING)
        .sortBy('currentEta')
        .value(),
    [filteredItems]
  );

  const onRouteItems = useMemo(
    () =>
      _.chain(filteredItems)
        .filter((item) => item.status == OrderShipmentStatus.ON_ROUTE)
        .sortBy('currentEta')
        .value(),
    [filteredItems]
  );

  const deliveredItems = useMemo(
    () =>
      _.chain(filteredItems)
        .filter((item) => item.status == OrderShipmentStatus.DELIVERED)
        .sortBy('arrivalTime')
        .reverse()
        .value(),
    [filteredItems]
  );

  const vessels = useMemo(() => {
    let options: string[] | undefined = items?.map((i) => i.vessel);
    if (!isMapVisible) {
      const vessels = _.uniq(options || []).sort();

      setSelectedVessels(vessels);

      return vessels;
    } else {
      switch (activeTab) {
        case 1:
          options = items?.filter((i) => i.status === OrderShipmentStatus.LOADING).map((i) => i.vessel);
          break;
        case 2:
          options = items?.filter((i) => i.status === OrderShipmentStatus.ON_ROUTE).map((i) => i.vessel);
          break;
        case 3:
          options = items?.filter((i) => i.status === OrderShipmentStatus.DELIVERED).map((i) => i.vessel);
          break;
      }

      let vessels = _.uniq(options || []).sort();
      if (!vessels.length) {
        // if no vessels are found for the tab, default to all vessels
        vessels = _.uniq(options || []).sort();
      }

      setSelectedVessels(vessels);
      return vessels;
    }
  }, [items, isMapVisible, activeTab]);

  const renderedItems = useMemo(() => {
    switch (activeTab) {
      case 1:
        return loadingItems;
      case 2:
        return onRouteItems;
      case 3:
        return deliveredItems;
      default:
        return items;
    }
  }, [activeTab, loadingItems, onRouteItems, deliveredItems, items]);

  //#endregion MEMO

  //#region HANDLERS

  const handleCheckAllSitesChange = () => {
    setSelectedSiteIds((prev) => (prev.length === sites?.length ? [] : siteIds));
  };

  const handleCheckAllVesselsChange = () => {
    setSelectedVessels((prev) => (prev.length === vessels?.length ? [] : vessels));
  };

  //#endregion HANDLERS

  return (
    <Box px={2}>
      <Alert severity="info" sx={{ marginTop: '-58px' }}>
        <FormattedMessage id={`${PAGE_NAME_ID}.columns.alert.disclaimer`} />
      </Alert>
      <FlexBox display={'flex'} flexDirection={'row'} justifyContent={'space-between'} mt={8} mb={8}>
        <Box width={'100%'} display={'flex'} justifyContent={'flex-start'} alignItems={'center'} gap={3}>
          <Select
            search={{
              value: searchValue,
              onChange: setSearchValue,
            }}
            multiple
            label={'Sites'}
            value={selectedSiteIds}
            onChange={(e) => setSelectedSiteIds(e.target.value as string[])}
            checkAll={{
              value: selectedSiteIds.length === siteIds.length,
              onChange: handleCheckAllSitesChange,
            }}>
            {sites
              .filter((s) => s.siteName.toLowerCase().includes(searchValue.toLowerCase()))
              .sort((a, b) => a.siteName.localeCompare(b.siteName))
              .map((site, idx) => (
                <CheckboxSelectItem key={idx} checked={selectedSiteIds.includes(site.globalSiteId)} value={site.globalSiteId}>
                  {site.siteName}
                </CheckboxSelectItem>
              ))}
          </Select>
          <Select
            multiple
            label={'Vessels'}
            value={selectedVessels}
            MenuProps={{
              autoFocus: false,
            }}
            onChange={(e) => setSelectedVessels(e.target.value as string[])}
            search={{
              value: vesselSearchValue,
              onChange: setVesselSearchValue,
            }}
            checkAll={{
              value: selectedVessels.length === vessels.length,
              onChange: handleCheckAllVesselsChange,
            }}>
            {vessels.map((vessel, idx) => (
              <CheckboxSelectItem key={idx} checked={selectedVessels.includes(vessel)} value={vessel}>
                {vessel}
              </CheckboxSelectItem>
            ))}
          </Select>
          {isMapVisible && (
            <Box>
              <Checkbox
                label={intl.formatMessage({
                  id: `${PAGE_NAME_ID}.showFactories`,
                })}
                checked={showFactoryLocations}
                onChange={() => setShowFactoryLocations((prev) => !prev)}
              />
            </Box>
          )}
        </Box>
        {!isMobile ? (
          <ButtonGroup
            value={'group'}
            buttons={[
              { label: <FormattedMessage id={`${PAGE_NAME_ID}.columns.columns`} />, value: 'left', state: false },
              {
                label: <FormattedMessage id={`${PAGE_NAME_ID}.columns.map`} />,
                value: 'center',
                state: true,
              },
            ]}
            handleClick={(e) => setIsMapVisible(e as boolean)}
          />
        ) : null}
      </FlexBox>
      {!isMapVisible ? (
        <Box display={'flex'} justifyContent={'center'} gap={4} ml={0} mt={4}>
          {!isMobile ? (
            <Box display={'flex'} gap={4} flex={1}>
              <Paper transparent sx={{ flex: 1 }}>
                <Box display={'flex'} justifyContent={'center'} mb={4}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.columns.loading`} />
                  <Typography ml={1}>({loadingItems?.length})</Typography>
                </Box>
                <ColumnsContainer isLoading={isLoading} data={loadingItems} />
              </Paper>
              <Paper transparent sx={{ flex: 1 }}>
                <Box display={'flex'} justifyContent={'center'} mb={4}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.columns.onRoute`} />
                  <Typography ml={1}>({onRouteItems?.length})</Typography>
                </Box>
                <ColumnsContainer isLoading={isLoading} data={onRouteItems} />
              </Paper>
              <Paper transparent sx={{ flex: 1 }}>
                <Box display={'flex'} justifyContent={'center'} mb={4}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.columns.delivered`} />
                  <Typography ml={1}>({deliveredItems?.length})</Typography>
                </Box>
                <ColumnsContainer isLoading={isLoading} data={deliveredItems} />
              </Paper>
            </Box>
          ) : (
            <Paper transparent sx={{ width: { xs: '100%', md: '70vh' } }}>
              <DeliveryColumnsSubTabs deliveredItems={deliveredItems} loadingItems={loadingItems} onRouteItems={onRouteItems} />
            </Paper>
          )}
        </Box>
      ) : (
        <Box display={'flex'} justifyContent={'center'} gap={4}>
          <Paper transparent sx={{ width: { xs: '100%', md: '70vh' } }}>
            <DeliveryColumnsSubTabs
              deliveredItems={deliveredItems}
              loadingItems={loadingItems}
              onRouteItems={onRouteItems}
              activeTabNumber={(tab: number) => setActiveTab(tab)}
            />
          </Paper>
          {!isMobile && (
            <MapGrid maxHeight={'70vh'}>
              <DeliveryBoardMap items={renderedItems || []} showVessels={activeTab === 2} showFactories={showFactoryLocations} />
            </MapGrid>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(DeliveryBoard);
